// REFACTORED: 2026-02-06
// FROM src/components/project/Projects.tsx
// Main projects dashboard with metrics, charts, and tables

// ============================================================================
// IMPORTS
// ============================================================================

// React & Core Libraries
import React, { useState, useEffect, useMemo, useCallback } from "react";

// MUI Components
import { Box, Grid, Paper, Skeleton } from "@mui/material";

// Query & Data Fetching
import { useQuery } from "@tanstack/react-query";
import { dashboardApi, getTableSyncStatus } from "../services/api";
import LastSyncedLabel from "../components/common/LastSyncedLabel";

// Contexts
import { useFilterOptions } from "../contexts/FilterOptionsContext";
import { useAuth } from "../contexts/AuthContext";

// Types
import { ProjectsFilterRequest, ProjectsFilterOptions } from "../types/projects";
import type {
  ProjectDetailsFilterOptionsResponse,
  ProjectDetailsFilterRequest,
} from "../types/api";

// Constants
import { excludeUnallocatedFromBus } from "../constants/buConstants";
import { hasAdminAccess } from "../utils/AdminLogic";
import { QUARTERS } from "../constants/ProjectConstants";
import { PROJECTS_QUERY_CONFIG } from "../constants/QueryConstants";

// Components - State
import ProjectsErrorState from "../components/project/ProjectsErrorState";

// Components - Sections
import ProjectsTopFilters from "../components/filters/page/ProjectsTopFilters";
import ProjectMetricsSection from "../components/project/sections/ProjectMetricsSection";
import ProjectChartsSection from "../components/project/sections/ProjectChartsSection";
import ProjectActionableInsights, {
  PROJECT_DETAILS_SCROLL_ID,
  getInsightTableFilterPatch,
  type ActionableInsightCardId,
} from "../components/project/ProjectActionableInsights";

// Components - Lazy loaded via LazyComponents
import { LazyProjectsAnalytics, LazyProjectDetailsAllocationSection } from "../components/LazyComponents";

// ============================================================================
// MAIN PROJECTS PAGE COMPONENT
// ============================================================================

const ProjectsPage: React.FC = () => {
  // ==========================================================================
  // STATE MANAGEMENT
  // ==========================================================================

  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;

  const { user } = useAuth();
  const isAdmin = hasAdminAccess(user);

  // Filter state - Separate current filters from applied filters
  const [currentFilters, setCurrentFilters] = useState<ProjectsFilterRequest>({
    opportunity_closed_years: [previousYear, currentYear],
    opportunity_closed_quarters: QUARTERS,
    opportunity_stage: [],
    project_account_bus: [],
    project_owners: [],
    delivery_bus: [],
    delivery_sub_bus: [],
    accounts: [],
    statuses: [],
    rag_statuses: [],
    efforts_deviation: [],
    smm_ratings: [],
    funded_by: [],
    project_revenue_converted: [],
  });

  // Applied filters - Used for actual API queries
  const [appliedFilters, setAppliedFilters] = useState<ProjectsFilterRequest>(currentFilters);

  // Filter options - Available values for each filter dropdown
  const [filterOptions, setFilterOptions] = useState<ProjectsFilterOptions>({
    opportunity_closed_years: [],
    opportunity_stage: [],
    project_owners: [],
    delivery_bus: [],
    delivery_sub_bus: [],
    accounts: [],
    project_account_bus: [],
    statuses: [],
    rag_statuses: [],
    efforts_deviation: [],
    smm_ratings: [],
    funded_by: [],
  });

  // Track if default filters have been set
  const [defaultsSet, setDefaultsSet] = useState(false);

  // UI state for additional filters toggle
  const [showAdditionalFilters, setShowAdditionalFilters] = useState<boolean>(false);

  // ==========================================================================
  // DATA FETCHING - Filter Options
  // ==========================================================================

  const {
    filterOptions: contextFilterOptions,
    isLoading: contextFilterOptionsLoading,
    error: contextFilterOptionsError,
    refetch: refetchContextFilters,
  } = useFilterOptions();

  const {
    data: additionalFilterOptions,
    isLoading: additionalFilterOptionsLoading,
    error: additionalFilterOptionsError,
    refetch: refetchAdditionalFilters,
  } = useQuery({
    queryKey: ["projects-additional-filter-options"],
    queryFn: async () => {
      const mainFilters = await dashboardApi.getFilterOptions(['time', 'projects', 'org']);
      // console.log('mainFilters', mainFilters);

      return {
        filterOptionsForPanel: {
          opportunity_closed_years: mainFilters.years || [],
          opportunity_stage: mainFilters.opportunity_stage || [],
          project_owners: mainFilters.project_owners || [],
          delivery_bus: mainFilters.business_units || [],
          delivery_sub_bus: mainFilters.sub_bus || [],
          accounts: mainFilters.accounts || [],
          project_account_bus: mainFilters.project_account_bus || [],
          statuses: mainFilters.project_statuses || [],
          rag_statuses: mainFilters.rag_statuses || [],
          efforts_deviation: mainFilters.efforts_deviation || [],
          smm_ratings: mainFilters.smm_ratings || [],
          funded_by: mainFilters.funded_by || [],
        },
        rawFilterOptions: mainFilters,
      };
    },
    ...PROJECTS_QUERY_CONFIG.filterOptions,
  });

  const {
    data: subBuOptions,
    isLoading: subBuLoading,
  } = useQuery({
    queryKey: ['sub-bu-options-projects', currentFilters.delivery_bus],
    queryFn: async () => {
      if (!currentFilters.delivery_bus || currentFilters.delivery_bus.length === 0) {
        return [];
      }
      return await dashboardApi.getSubBusByBusinessUnits(currentFilters.delivery_bus);
    },
    enabled: !!(currentFilters.delivery_bus && currentFilters.delivery_bus.length > 0),
    ...PROJECTS_QUERY_CONFIG.subBuOptions,
  });

  // ==========================================================================
  // MEMOIZED VALUES
  // ==========================================================================

  const availableSubBus = useMemo(() => {
    return currentFilters.delivery_bus && currentFilters.delivery_bus.length > 0
      ? (subBuOptions || [])
      : filterOptions.delivery_sub_bus;
  }, [currentFilters.delivery_bus, subBuOptions, filterOptions.delivery_sub_bus]);

  const availableSubBusKey = useMemo(() => availableSubBus.join(','), [availableSubBus]);
  const businessUnitsKey = useMemo(() => currentFilters.delivery_bus?.join(',') || '', [currentFilters.delivery_bus]);
  const subBusOptionsKey = useMemo(() => filterOptions.delivery_sub_bus.join(','), [filterOptions.delivery_sub_bus]);

  const filterOptionsLoading = contextFilterOptionsLoading || additionalFilterOptionsLoading;
  const filterOptionsError = contextFilterOptionsError || additionalFilterOptionsError;
  const refetchFilters = () => {
    refetchContextFilters();
    refetchAdditionalFilters();
  };

  const apiFilterOptions = useMemo(() => {
    if (!additionalFilterOptions?.filterOptionsForPanel) return undefined;
    const panel = additionalFilterOptions.filterOptionsForPanel;
    return {
      ...panel,
      delivery_bus: panel.delivery_bus || [],
      delivery_sub_bus: panel.delivery_sub_bus || [],
    };
  }, [additionalFilterOptions]);

  const projectDetailsFilterOptions = useMemo((): ProjectDetailsFilterOptionsResponse | undefined => {
    const raw = additionalFilterOptions?.rawFilterOptions;
    if (!raw) return undefined;
    return {
      project_codes: raw.project_codes || [],
      project_names: [],
      accounts: raw.accounts || [],
      project_account_bus: raw.project_account_bus || [],
      delivery_bus: raw.delivery_bus || raw.business_units || [],
      delivery_sub_bus: raw.delivery_sub_bus || raw.sub_bus || [],
      project_types: [],
      domains: [],
      statuses: raw.project_statuses || raw.statuses || [],
      rag_statuses: raw.rag_statuses || [],
      funded_by: raw.funded_by || [],
      project_owners: raw.project_owners || [],
      opportunity_closed_years: raw.years || [],
      opportunity_closed_quarters: raw.opportunity_closed_quarters || [],
      opportunity_stage: raw.opportunity_stage || [],
      owners: raw.project_owners || [],
      efforts_deviation: raw.efforts_deviation || [],
      smm_ratings: raw.smm_ratings || [],
      regions: [],
    };
  }, [additionalFilterOptions]);

  const filtersChanged = JSON.stringify(currentFilters) !== JSON.stringify(appliedFilters);

  const [insightTableApply, setInsightTableApply] = useState<{
    requestId: number;
    patch: Partial<ProjectDetailsFilterRequest>;
  } | null>(null);

  const appliedFiltersSignature = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters]);

  const handleNavigateInsight = useCallback((cardId: ActionableInsightCardId) => {
    setInsightTableApply({
      requestId: Date.now(),
      patch: getInsightTableFilterPatch(cardId),
    });
    requestAnimationFrame(() => {
      setTimeout(() => {
        document.getElementById(PROJECT_DETAILS_SCROLL_ID)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 320);
    });
  }, []);

  // ==========================================================================
  // EFFECTS
  // ==========================================================================

  useEffect(() => {
    setInsightTableApply(null);
  }, [appliedFiltersSignature]);

  useEffect(() => {
    if (currentFilters.delivery_sub_bus && currentFilters.delivery_sub_bus.length > 0 && !subBuLoading) {
      const validSubBUs = currentFilters.delivery_sub_bus.filter(item => {
        if (currentFilters.delivery_bus && currentFilters.delivery_bus.length > 0) {
          return availableSubBus.includes(item);
        } else {
          return filterOptions.delivery_sub_bus.includes(item);
        }
      });

      if (validSubBUs.length !== currentFilters.delivery_sub_bus.length) {
        setCurrentFilters({
          ...currentFilters,
          delivery_sub_bus: validSubBUs,
        });
      }
    }
  }, [availableSubBusKey, businessUnitsKey, subBusOptionsKey, subBuLoading, currentFilters.delivery_sub_bus]);

  useEffect(() => {
    if (apiFilterOptions) {
      const currentKeys = Object.keys(filterOptions).sort();
      const newKeys = Object.keys(apiFilterOptions).sort();
      const keysMatch = JSON.stringify(currentKeys) === JSON.stringify(newKeys);

      let hasChanged = !keysMatch;
      if (keysMatch) {
        for (const key of currentKeys) {
          const currentValue = filterOptions[key as keyof typeof filterOptions];
          const newValue = apiFilterOptions[key as keyof typeof apiFilterOptions];
          if (JSON.stringify(currentValue) !== JSON.stringify(newValue)) {
            hasChanged = true;
            break;
          }
        }
      }

      if (hasChanged) {
        setFilterOptions(apiFilterOptions);
      }

      if (!defaultsSet) {
        // Auto-fill user's BUs and SBUs on load (same as Utilization page)
        const defaultDeliveryBus = isAdmin
          ? apiFilterOptions.delivery_bus || []
          : excludeUnallocatedFromBus(apiFilterOptions.delivery_bus);
        if (defaultDeliveryBus.length > 0) {
          setCurrentFilters((prev) => ({
            ...prev,
            delivery_bus: [...defaultDeliveryBus],
            delivery_sub_bus: (apiFilterOptions.delivery_sub_bus?.length ?? 0) > 0
              ? [...apiFilterOptions.delivery_sub_bus]
              : prev.delivery_sub_bus,
          }));
          setAppliedFilters((prev) => ({
            ...prev,
            delivery_bus: [...defaultDeliveryBus],
            delivery_sub_bus: (apiFilterOptions.delivery_sub_bus?.length ?? 0) > 0
              ? [...apiFilterOptions.delivery_sub_bus]
              : prev.delivery_sub_bus,
          }));
        }

        // Categorical multi-select filters intentionally start EMPTY (= no WHERE clause = show all).
        // Pre-filling with all available values looks like "show all" but the backend interprets
        // a non-empty list as a restrictive IN clause, which silently drops rows where the column
        // is NULL. smm_ratings is especially bad because the backend adds IS NOT NULL explicitly.

        setCurrentFilters((prev) => ({ ...prev, opportunity_closed_quarters: QUARTERS }));
        setAppliedFilters((prev) => ({ ...prev, opportunity_closed_quarters: QUARTERS }));

        if (apiFilterOptions.opportunity_closed_years?.length > 0) {
          const defaultYears: number[] = [];
          if (apiFilterOptions.opportunity_closed_years.includes(previousYear)) {
            defaultYears.push(previousYear);
          }
          if (apiFilterOptions.opportunity_closed_years.includes(currentYear)) {
            defaultYears.push(currentYear);
          }

          const defaultYear = defaultYears.length > 0
            ? defaultYears
            : [apiFilterOptions.opportunity_closed_years[apiFilterOptions.opportunity_closed_years.length - 1]];

          setCurrentFilters((prev) => ({ ...prev, opportunity_closed_years: defaultYear }));
          setAppliedFilters((prev) => ({ ...prev, opportunity_closed_years: defaultYear }));
        }

        setDefaultsSet(true);
      }
    }
  }, [apiFilterOptions, defaultsSet, additionalFilterOptionsLoading, isAdmin]);

  // Allow dashboard to run when filter options fail/empty so we don't block forever
  useEffect(() => {
    if (!additionalFilterOptionsLoading && !defaultsSet) {
      setDefaultsSet(true);
    }
  }, [additionalFilterOptionsLoading, defaultsSet]);

  // ==========================================================================
  // DATA FETCHING - Projects Dashboard (separate queries per section)
  // ==========================================================================

  const dashboardConfig = { enabled: defaultsSet, ...PROJECTS_QUERY_CONFIG.dashboard };

  const summaryMetricsQuery = useQuery({
    queryKey: ["projects-summary-metrics", appliedFilters],
    queryFn: () => dashboardApi.getProjectsSummaryMetrics(appliedFilters),
    ...dashboardConfig,
  });

  const projectMetricsQuery = useQuery({
    queryKey: ["projects-metrics", appliedFilters],
    queryFn: () => dashboardApi.getProjectsProjectMetrics(appliedFilters),
    ...dashboardConfig,
  });

  const fundingAnalysisQuery = useQuery({
    queryKey: ["projects-funding-analysis", appliedFilters],
    queryFn: () => dashboardApi.getProjectsFundingAnalytics(appliedFilters),
    ...dashboardConfig,
  });

  const healthMetricsQuery = useQuery({
    queryKey: ["projects-health-metrics", appliedFilters],
    queryFn: () => dashboardApi.getProjectsHealthMetrics(appliedFilters),
    ...dashboardConfig,
  });

  const regionalAnalyticsQuery = useQuery({
    queryKey: ["projects-regional-analytics", appliedFilters],
    queryFn: () => dashboardApi.getProjectsRegionalAnalytics(appliedFilters),
    ...dashboardConfig,
  });

  const accountPerformanceQuery = useQuery({
    queryKey: ["projects-account-performance", appliedFilters],
    queryFn: () => dashboardApi.getProjectsAccountPerformance(appliedFilters),
    ...dashboardConfig,
  });

  const ragHealthMatrixQuery = useQuery({
    queryKey: ["projects-rag-health", appliedFilters],
    queryFn: () => dashboardApi.getProjectsRagHealthMetrics(appliedFilters),
    ...dashboardConfig,
  });

  const projectHealthScoreQuery = useQuery({
    queryKey: ["projects-health-score", appliedFilters],
    queryFn: () => dashboardApi.getProjectsProjectHealthMetrics(appliedFilters),
    ...dashboardConfig,
  });

  const { data: syncData, isLoading: syncLoading } = useQuery({
    queryKey: ["sync-status"],
    queryFn: getTableSyncStatus,
    staleTime: 10 * 60 * 1000,
  });

  const anyDashboardError =
    summaryMetricsQuery.error ||
    projectMetricsQuery.error ||
    fundingAnalysisQuery.error ||
    healthMetricsQuery.error ||
    regionalAnalyticsQuery.error ||
    accountPerformanceQuery.error ||
    ragHealthMatrixQuery.error ||
    projectHealthScoreQuery.error;

  const refetchAllDashboard = () => {
    summaryMetricsQuery.refetch();
    projectMetricsQuery.refetch();
    fundingAnalysisQuery.refetch();
    healthMetricsQuery.refetch();
    regionalAnalyticsQuery.refetch();
    accountPerformanceQuery.refetch();
    ragHealthMatrixQuery.refetch();
    projectHealthScoreQuery.refetch();
  };

  // ==========================================================================
  // EVENT HANDLERS
  // ==========================================================================

  /** Drop blank SBU tokens and SBUs that are not valid for the selected BU(s) before API calls. */
  const normalizeDeliverySubBusForApply = useCallback(
    (filters: ProjectsFilterRequest): ProjectsFilterRequest => {
      const bus = filters.delivery_bus || [];
      const allowedSub =
        bus.length > 0
          ? (availableSubBus || []).filter((s) => s != null && String(s).trim() !== "")
          : (filterOptions.delivery_sub_bus || []).filter((s) => s != null && String(s).trim() !== "");
      const rawSub = filters.delivery_sub_bus || [];
      const delivery_sub_bus = rawSub.filter(
        (s) => s != null && String(s).trim() !== "" && allowedSub.includes(String(s))
      );
      return { ...filters, delivery_sub_bus };
    },
    [availableSubBus, filterOptions.delivery_sub_bus]
  );

  const handleRefresh = useCallback(() => {
    const normalized = normalizeDeliverySubBusForApply(currentFilters);
    setCurrentFilters(normalized);
    setAppliedFilters(normalized);
  }, [currentFilters, normalizeDeliverySubBusForApply]);

  const handleClearFilters = () => {
    const defaultYears: number[] = [];
    if (filterOptions.opportunity_closed_years?.includes(previousYear)) {
      defaultYears.push(previousYear);
    }
    if (filterOptions.opportunity_closed_years?.includes(currentYear)) {
      defaultYears.push(currentYear);
    }

    const defaultYear = defaultYears.length > 0
      ? defaultYears
      : filterOptions.opportunity_closed_years?.length > 0
        ? [filterOptions.opportunity_closed_years[filterOptions.opportunity_closed_years.length - 1]]
        : [previousYear, currentYear];

    const clearedFilters: ProjectsFilterRequest = {
      opportunity_closed_years: defaultYear,
      opportunity_closed_quarters: [],
      opportunity_stage: [],
      project_owners: [],
      delivery_bus: [],
      delivery_sub_bus: [],
      accounts: [],
      project_account_bus: [],
      statuses: [],
      rag_statuses: [],
      efforts_deviation: [],
      smm_ratings: [],
      funded_by: [],
      project_revenue_converted: [],
    };
    
    setCurrentFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  if (anyDashboardError) {
    return <ProjectsErrorState onRetry={refetchAllDashboard} />;
  }

  const defaultSummaryMetrics = {
    total_projects: 0,
    internal_projects: 0,
    open_opportunities: 0,
    avg_recognition: 0,
    total_booked_revenue: 0,
    total_recognized_revenue: 0,
    total_realized_revenue: 0,
  };
  const summaryMetrics = summaryMetricsQuery.data ?? defaultSummaryMetrics;

  const defaultStatusCounts = {
    active: 0,
    yet_to_start: 0,
    completed: 0,
    on_hold: 0,
    cancelled: 0,
    delayed_starts: 0,
  };
  const revenueByStatus = projectMetricsQuery.data?.revenue_by_status ?? [];
  const statusCounts =
    revenueByStatus.length > 0
      ? revenueByStatus.reduce(
          (acc: typeof defaultStatusCounts, item: { status: string; project_count: number }) => {
            const key =
              item.status === "Active"
                ? "active"
                : item.status === "Yet to Start"
                  ? "yet_to_start"
                  : item.status === "Completed"
                    ? "completed"
                    : item.status === "On Hold"
                      ? "on_hold"
                      : item.status === "Cancelled"
                        ? "cancelled"
                        : item.status === "Delayed Starts"
                          ? "delayed_starts"
                          : null;
            if (key) acc[key] = item.project_count;
            return acc;
          },
          { ...defaultStatusCounts }
        )
      : defaultStatusCounts;

  const defaultHealthMetrics = {
    timeline_breaches_in_progress: 0,
    timeline_breaches_completed: 0,
    under_budget_in_progress: 0,
    under_budget_completed: 0,
    over_budget_in_progress: 0,
    over_budget_completed: 0,
  };
  const healthMetrics = healthMetricsQuery.data ?? defaultHealthMetrics;

  const analyticsLoading =
    healthMetricsQuery.isLoading ||
    regionalAnalyticsQuery.isLoading ||
    fundingAnalysisQuery.isLoading ||
    accountPerformanceQuery.isLoading ||
    ragHealthMatrixQuery.isLoading ||
    projectHealthScoreQuery.isLoading;

  const analytics =
    healthMetricsQuery.data != null &&
    regionalAnalyticsQuery.data != null &&
    fundingAnalysisQuery.data != null &&
    accountPerformanceQuery.data != null &&
    ragHealthMatrixQuery.data != null &&
    projectHealthScoreQuery.data != null
      ? {
          regional_analytics: regionalAnalyticsQuery.data ?? [],
          rag_health_matrix: ragHealthMatrixQuery.data ?? [],
          revenue_by_status: revenueByStatus,
          funding_analysis: fundingAnalysisQuery.data ?? [],
          project_health_score: projectHealthScoreQuery.data ?? {
            avg_timeline_health: 0,
            avg_budget_health: 0,
            overall_health: 0,
          },
          account_performance: accountPerformanceQuery.data ?? [],
        }
      : null;

  const sectionSx = {
    p: 2,
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    backgroundColor: "background.paper",
    boxShadow: "none",
  };

  return (
    <Box sx={{ px: 2, pt: 2, pb: 2 }}>
      <Box sx={{ mb: 2 }}>
        <ProjectsTopFilters
          currentFilters={currentFilters}
          setCurrentFilters={setCurrentFilters}
          filterOptions={filterOptions}
          availableSubBus={availableSubBus}
          filterOptionsLoading={filterOptionsLoading}
          subBuLoading={subBuLoading}
          filterOptionsError={filterOptionsError}
          refetchFilters={refetchFilters}
          showAdditionalFilters={showAdditionalFilters}
          setShowAdditionalFilters={setShowAdditionalFilters}
          filtersChanged={filtersChanged}
          handleRefresh={handleRefresh}
          handleClearFilters={handleClearFilters}
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <LastSyncedLabel isoTimestamp={syncData?.projects} isLoading={syncLoading} />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper elevation={0} sx={sectionSx}>
            <ProjectMetricsSection
              totalProjects={summaryMetrics.total_projects}
              internalProjects={summaryMetrics.internal_projects}
              totalBookedRevenue={summaryMetrics.total_booked_revenue}
              totalRecognizedRevenue={summaryMetrics.total_recognized_revenue}
              totalRealizedRevenue={summaryMetrics.total_realized_revenue}
              isLoading={summaryMetricsQuery.isLoading || !defaultsSet}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={0} sx={sectionSx}>
            <ProjectActionableInsights
              projectFilters={appliedFilters}
              filterOptionsReady={defaultsSet && !!projectDetailsFilterOptions}
              onNavigateWithInsight={handleNavigateInsight}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={0} sx={sectionSx}>
            <ProjectChartsSection
              summaryMetrics={summaryMetrics}
              rag_health_matrix={ragHealthMatrixQuery.data ?? []}
              statusCounts={statusCounts}
              fundingAnalysis={fundingAnalysisQuery.data ?? undefined}
              isLoading={
                (summaryMetricsQuery.isLoading ||
                  projectMetricsQuery.isLoading ||
                  fundingAnalysisQuery.isLoading ||
                  ragHealthMatrixQuery.isLoading) ||
                !defaultsSet
              }
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={0} sx={sectionSx}>
            {analyticsLoading || !defaultsSet ? (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Skeleton variant="text" width={220} height={28} sx={{ mb: 1 }} animation="wave" />
                  <Skeleton variant="rectangular" width={64} height={3} sx={{ borderRadius: 1 }} animation="wave" />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} animation="wave" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} animation="wave" />
                  </Grid>
                </Grid>
              </Box>
            ) : analytics != null ? (
              <LazyProjectsAnalytics
                analytics={analytics}
                healthMetrics={healthMetrics}
                excludeRegionalPerformance={true}
                filters={appliedFilters}
              />
            ) : null}
          </Paper>
        </Grid>

        <Grid item xs={12} id={PROJECT_DETAILS_SCROLL_ID}>
          <Paper elevation={0} sx={{ ...sectionSx, p: 0 }}>
            <LazyProjectDetailsAllocationSection
              projectFilters={appliedFilters}
              projectDetailsFilterOptions={projectDetailsFilterOptions}
              insightTableApply={insightTableApply}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectsPage;
